<script setup lang="ts">
import {
    computed,
    ref,
    onMounted,
    watch,
} from 'vue';
import {
    type ValidationRule,
    type QInput,
    type QInputProps,
    useQuasar,
    QForm,
} from 'quasar';
import slugify from 'slugify';
import type { AxiosError } from 'axios';
import type FlightSchool from '@/types/FlightSchool';
import type { FlightSchoolForm } from '@/types/FlightSchoolForm';
import StateSelect from '@/components/Utilities/StateSelect.vue';
import FlightSchoolRepository from '@/repositories/FlightSchoolRepository';
import type { FlightSchoolResponse } from '@/types/Response/FlightSchoolResponse';
import AdminRepository from '@/repositories/AdminRepository';
import { useSchoolStore } from '@/stores/school';
import router from '@/router';
import { isStandardErrorResponse, isValidationExceptionResponse, type ErrorResponse } from '@/types/Response/StandardErrorResponse';
import CertificateRepository from '@/repositories/CertificateRepository';
import type { Certificate, AvailableParts } from '@/types/Certificate';

const $q = useQuasar();
const props = defineProps<{
    formData: FlightSchoolForm
    buttonLabel: 'Save' | 'Create School'
    originalId?: number
}>();

const emailInput = ref<QInput>();
const schoolData = ref<FlightSchoolForm>(props.formData);
const successLabel = computed(() => (props.buttonLabel === 'Save' ? 'changes saved successfully' : 'created successfully'));
const visibilityString = ref(schoolData.value.isVisible === null ? '' : schoolData.value.isVisible.toString());
const isExistingFlightSchool = ref(false);
const isLoading = ref(false);
const openedPartSections = ref<string[]>([]);

const allCertificates = ref<{
    [key in AvailableParts]: Certificate[];
}>({
    141: [],
    61: [],
});

const selectedCertificates = ref<{
    [key in AvailableParts]: string[];
}>({
    141: [],
    61: [],
});

const ruleNotRequired = () => true;
const ruleNotEmpty = (val:string) => ((val && val.length > 0) || 'This field is required');
const ruleOnlyNumbers = (val:string) => (/^[0-9]*$/.test(val) || 'Only numbers are allowed');
const ruleUniqueFlightSchool = () => !isExistingFlightSchool.value || 'A school with that name already exists';
const resetUniqueFlightSchool = () => { isExistingFlightSchool.value = false; };
const ruleNotOnlySpaces = (val:string) => (val.trim().length > 0 || 'This field cannot only contain spaces');
const rulePhoneNumberLength = (val:string) => (val.length === 10 || 'Phone number must be 10 digits.');
const ruleZipCodeLength = (val:string) => ((val.length >= 5 && val.length <= 15) || 'Zip code must be 5 to 15 digits.');
const ruleEmailAddress = (val:string) => (val.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) !== null || 'Invalid email address.');

type CheckedEmails = {
    email: string;
    valid: boolean;
    reason?: string;
};

const checkedEmails = ref<CheckedEmails[]>([]);

/**
 * Validates most inputs that display error banners on the form.
 *
 * @return {boolean} Whether the checked inputs are valid
 */
const performPreSubmissionValidation = () => {
    if (schoolData.value.certificateUuids.length === 0) {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
            message: 'Please select at least one course',
        });

        return false;
    }
    if (visibilityString.value === '') {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
            message: 'Please choose a visibility setting',
        });

        return false;
    }

    return true;
};

const slugifySchoolName = (schoolName: string): string => {
    const slug = slugify(schoolName, {
        lower: true,
        strict: true,
    });

    return slug;
};

const defaultInputProps: Omit<QInputProps, 'modelValue'> = {
    type: 'text',
    dense: true,
    outlined: true,
    lazyRules: true,
    rules: [ruleNotEmpty, ruleNotOnlySpaces],
};

const createSchool = async ({
    name,
    address,
    phone,
    certificateUuids,
    courseAcronyms,
    email,
    isVisible,
    isArchived,
}: FlightSchoolForm) : Promise<FlightSchoolResponse> => {
    const urlSlug = slugifySchoolName(name);

    const flightSchoolRequest: Omit<FlightSchool, 'id' | 'courses' | 'owner'> = {
        name,
        urlSlug,
        address,
        phone,
        isVisible: isVisible as boolean,
        isArchived,
    };

    return FlightSchoolRepository.createSchool(flightSchoolRequest, courseAcronyms, certificateUuids, email);
};

const updateSchool = async (
    {
        name,
        address,
        phone,
        certificateUuids,
        courseAcronyms,
        email,
        isVisible,
        isArchived,
    }: FlightSchoolForm,
) : Promise<FlightSchoolResponse> => {
    if (!props.originalId) {
        throw new Error('Cannot update school without an ID');
    }

    // attempt to check whether the name has changed
    let urlSlug = slugifySchoolName(name);
    const originalSchool = useSchoolStore().schools.find(
        (school) => school.id === props.originalId,
    );

    // don't risk changing the slug if the name hasn't changed
    if (originalSchool !== undefined && originalSchool.name === name) {
        urlSlug = originalSchool?.urlSlug;
    }

    const flightSchoolRequest : Omit<FlightSchool, 'courses' | 'owner'> = {
        id: props.originalId,
        name,
        urlSlug,
        address,
        phone,
        isVisible: isVisible as boolean,
        isArchived,
    };

    return FlightSchoolRepository.updateSchool(flightSchoolRequest, courseAcronyms, certificateUuids, email);
};

/**
 * Store the school in the store and redirect to the school profile page
 */
const storeSchoolThenRedirect = (school: FlightSchoolResponse) => {
    const schoolStore = useSchoolStore();

    schoolStore.setSchools([school, ...schoolStore.schools]);
    schoolStore.setSchool({
        ...school,
        courses: [],
    });

    router.push({ name: 'schoolProfile', params: { schoolSlug: school.urlSlug } });
};

/**
 * TODO: deprecate this function
 *
 * This function is used to maintain backwards compatibility with the old api methods, which required
 * course acronyms to be passed in as a separate parameter. The certificateUuids parameter should be
 * used instead.
 */
const getCourseAcronymsByCertificateUuids = (certificateUuids: string[]): string[] => {
    const flattenedCertificates = Object.values(allCertificates.value).flat();
    const courseAcronyms = flattenedCertificates
        .filter((cert) => certificateUuids.includes(cert.uuid))
        .map((cert) => cert.acronym);

    // Remove duplicates
    return Array.from(new Set(courseAcronyms));
};

const onSubmit = async () => {
    schoolData.value.certificateUuids = Object.values(selectedCertificates.value).flat();
    schoolData.value.courseAcronyms = getCourseAcronymsByCertificateUuids(schoolData.value.certificateUuids);

    if (performPreSubmissionValidation()) {
        isLoading.value = true;
        schoolData.value.isVisible = visibilityString.value === 'true';
        const schoolFunction = props.buttonLabel === 'Save' ? updateSchool : createSchool;

        const newSchool = await schoolFunction(schoolData.value).catch((
            reason: AxiosError<ErrorResponse>,
        ) => {
            const { data } = reason.response as { data: ErrorResponse };

            if (isStandardErrorResponse(data) && data.status === 409) {
                $q.notify({
                    color: 'red-5',
                    textColor: 'white',
                    icon: 'warning',
                    message: 'A school with that name already exists',
                });

                // Toggle error message under the Flight School Name input
                isExistingFlightSchool.value = true;
            } else if (isValidationExceptionResponse(data)) {
                data.context.violations.forEach(
                    (element: { field: string, message: string; }) => {
                        $q.notify({
                            color: 'red-5',
                            textColor: 'white',
                            icon: 'warning',
                            message: element.message,
                        });
                    },
                );
            } else {
                $q.notify({
                    color: 'red-5',
                    textColor: 'white',
                    icon: 'warning',
                    message: 'Something went wrong',
                });
            }
            isLoading.value = false;
        });
        if (isExistingFlightSchool.value) {
            document.getElementById('submit-btn')?.click();
            document.querySelector<HTMLInputElement>('input[class=school-name]')?.focus();
        }
        if (newSchool) {
            isLoading.value = false;
            storeSchoolThenRedirect(newSchool);

            $q.notify({
                color: 'green-4',
                textColor: 'white',
                icon: 'cloud_done',
                message: `School ${successLabel.value}`,
            });
        }
    }
};

// validate that an account with the given email exists then trigger confirmation dialog
const ruleAccountExists = async (val: string) => {
    let message = `${val} cannot be assigned to this school. Please double check the address and try again.`;

    let checkedEmail = checkedEmails.value.find((input) => input.email === val);
    if (!checkedEmail) {
        const { valid, reason } = await AdminRepository.validateAccountExists(val);
        checkedEmail = {
            email: val,
            valid,
            reason,
        };

        checkedEmails.value.push(checkedEmail);
    }

    if (checkedEmail?.reason === 'Account not found.') {
        message = `Gleim account not found. A Gleim account must be created for ${val} at `
            + 'gleim.com/account before adding them to your school.';
    }

    return checkedEmail.valid || message;
};

const emailRules = ref<ValidationRule[]>([
    ruleNotEmpty,
    ruleEmailAddress,
    ruleAccountExists,
]);

const areAllCertificatesSelected = computed(() => {
    const allCertificatesArray = Object.values(allCertificates.value).flat();
    const selectedCertificatesArray = Object.values(selectedCertificates.value).flat();

    return allCertificatesArray.every((cert) => selectedCertificatesArray.includes(cert.uuid));
});

const areAllCertificatesSelectedInPart = (part: number) => (
    allCertificates
        .value[part as AvailableParts]
        .every((cert) => selectedCertificates.value[part as AvailableParts].includes(cert.uuid))
);

const selectAllCertificates = (part?: number) => {
    if (part) {
        selectedCertificates.value[part as AvailableParts] = [
            ...allCertificates.value[part as AvailableParts].map((cert) => cert.uuid),
        ];
        // Ensure section is visible when selecting certificates
        if (!openedPartSections.value.includes(part.toString())) {
            openedPartSections.value.push(part.toString());
        }
    } else {
        // Global select all: select all certificates in all parts
        selectedCertificates.value = {
            141: allCertificates.value[141].map((cert) => cert.uuid),
            61: allCertificates.value[61].map((cert) => cert.uuid),
        };

        openedPartSections.value = Object.keys(selectedCertificates.value);
    }
};

const deselectAllCertificates = (part?: number) => {
    if (part) {
        selectedCertificates.value[part as AvailableParts] = [];
    } else {
        selectedCertificates.value = {
            141: [],
            61: [],
        };

        openedPartSections.value = [];
    }
};

const toggleCertificateSelection = (part?: number): void => {
    if (part) {
        if (areAllCertificatesSelectedInPart(part)) {
            deselectAllCertificates(part);
            return;
        }
        selectAllCertificates(part);
    } else {
        if (areAllCertificatesSelected.value) {
            deselectAllCertificates();
            return;
        }
        selectAllCertificates();
    }
};

watch(openedPartSections, (newVal, oldVal) => {
    // check if a part was removed from openedPartSections
    const removedPart = oldVal.filter((p) => !newVal.includes(p));
    if (removedPart.length > 0) {
        // clear the certificates for the removed part
        removedPart.forEach((part) => {
            deselectAllCertificates(Number(part));
        });
    }
});

const clearOrSelectVerbage = (part?: number) => {
    if (part) {
        return areAllCertificatesSelectedInPart(part)
            ? `Clear All Part ${part} Courses`
            : `Select All Part ${part} Courses`;
    }
    return areAllCertificatesSelected.value ? 'Clear All' : 'Select All';
};

onMounted(async () => {
    const certificates = await CertificateRepository.getAllCertificates();

    certificates.forEach((certificate) => {
        if (certificate.part in allCertificates.value) {
            allCertificates.value[certificate.part as AvailableParts].push(certificate);
        }
    });

    selectedCertificates.value = {
        141: allCertificates.value[141]
            .filter((cert) => schoolData.value.certificateUuids.includes(cert.uuid))
            .map((cert) => cert.uuid),
        61: allCertificates.value[61]
            .filter((cert) => schoolData.value.certificateUuids.includes(cert.uuid))
            .map((cert) => cert.uuid),
    };

    // Initialize openedPartSections to the parts that have selected certificates
    openedPartSections.value = Object.keys(selectedCertificates.value)
        .filter((part) => selectedCertificates.value[Number(part) as AvailableParts].length > 0);
});

</script>
<template>
    <q-form
        id="school-form"
        class="q-gutter-md bg-white q-pa-md q-mx-auto q-my-auto"
        @submit="onSubmit"
    >
        <div class="warning q-pa-xs q-mr-lg">
            <span class="warning-icon q-px-sm material-icons">info</span>
            All fields are required.
        </div>
        <q-input
            v-model="schoolData.name"
            label="Flight School Name"
            v-bind="defaultInputProps"
            class="q-pt-md school-name"
            maxlength="255"
            :rules="[ruleNotEmpty, ruleNotOnlySpaces, ruleUniqueFlightSchool]"
            @update:model-value="resetUniqueFlightSchool()"
        />
        <q-input
            ref="emailInput"
            v-model="schoolData.email"
            label="Primary Administrator Email Address"
            v-bind="defaultInputProps"
            :rules="emailRules"
            type="email"
            class="email"
            maxlength="255"
        />
        <div class="flex justify-between">
            <div
                id="realm-label"
                class="input-label q-pb-none q-pt-sm text-weight-bold q-mt-none"
            >
                Select Part and Courses
            </div>
            <q-btn
                id="select-clear-all-btn"
                :label="clearOrSelectVerbage()"
                class="anchor-button text-weight-regular text-fsum-med-blue"
                size="16px"
                no-caps
                flat
                :ripple="false"
                @click="toggleCertificateSelection()"
            />
        </div>
        <div class="row q-pb-md">
            <div
                v-for="(certificates, part) in allCertificates"
                :key="part"
                class="col-12 text-left cert-list q-gutter-y-sm"
            >
                <q-checkbox
                    v-model="openedPartSections"
                    class="row"
                    :class="`part-${part}-checkbox`"
                    color="fsum-green"
                    :val="part"
                    :label="`${part}`"
                    dense
                />
                <div
                    v-show="openedPartSections.includes(part.toString())"
                    class="q-ml-md row"
                    :class="{'visible': openedPartSections.includes(part.toString())}"
                >
                    <q-btn
                        :label="clearOrSelectVerbage(part)"
                        class="anchor-button col-12 text-weight-regular text-fsum-med-blue q-pl-sm"
                        :class="`part-${part}-clear-select-btn`"
                        no-caps
                        flat
                        size="16px"
                        dense
                        :ripple="false"
                        align="left"
                        @click="toggleCertificateSelection(part)"
                    />
                    <div class="checkbox-group q-mb-sm">
                        <q-checkbox
                            v-for="certificate in certificates"
                            :key="certificate.uuid"
                            v-model="selectedCertificates[part as AvailableParts]"
                            class="col-12 course-checkbox"
                            color="fsum-green"
                            :val="certificate.uuid"
                            :label="certificate.title"
                        />
                    </div>
                </div>
            </div>
        </div>
        <q-input
            v-model="schoolData.address.line1"
            label="Address"
            v-bind="defaultInputProps"
            maxlength="80"
            class="address1"
        />
        <q-input
            v-model="schoolData.address.line2"
            label="Address Line 2"
            v-bind="defaultInputProps"
            maxlength="80"
            :rules="[ruleNotRequired]"
            class="address2"
        />
        <q-input
            v-model="schoolData.address.city"
            label="City"
            v-bind="defaultInputProps"
            maxlength="50"
            class="city"
        />
        <div class="row">
            <div class="q-pr-lg col-5">
                <StateSelect
                    v-model="schoolData.address.state"
                />
            </div>
            <div class="col-7">
                <q-input
                    v-model="schoolData.address.country"
                    label="Country Code"
                    v-bind="defaultInputProps"
                    class="country"
                    disable
                />
            </div>
        </div>
        <div class="row">
            <div class="q-pr-sm col-6">
                <q-input
                    v-model="schoolData.address.postcode"
                    label="Zip"
                    v-bind="defaultInputProps"
                    inputmode="numeric"
                    minlenght="5"
                    maxlength="15"
                    :rules="[ruleOnlyNumbers, ruleZipCodeLength]"
                    class="zip"
                />
            </div>
            <div class="col-6">
                <q-input
                    v-model="schoolData.phone"
                    label="Phone"
                    class="phone"
                    type="tel"
                    v-bind="defaultInputProps"
                    mask="(###) ### - ####"
                    :rules="[rulePhoneNumberLength]"
                    unmasked-value
                />
            </div>
        </div>
        <label
            id="radio-visibility-label'"
            class="input-label q-pr-none"
        >
            Is Visible
        </label>
        <div class="row q-pl-none q-ml-sm">
            <q-radio
                id="radio-school-visible"
                v-model="visibilityString"
                class="vis-radio float-left q-pr-lg q-pb-lg"
                val="true"
                label="Yes"
            />
            <q-radio
                id="radio-school-invisible"
                v-model="visibilityString"
                class="vis-radio float-left q-pb-lg"
                val="false"
                label="No"
            />
        </div>
        <div class="row q-pb-md q-mr-md">
            <div class="col-6">
                <q-btn
                    id="back-btn"
                    label="Go Back"
                    type="button"
                    flat
                    class="back-btn fsum-button-outlined q-px-sm float-left"
                    icon="chevron_left"
                    no-caps
                    @click="$router.back()"
                />
            </div>
            <div class="col-6">
                <q-btn
                    id="submit-btn"
                    :label="buttonLabel"
                    type="submit"
                    class="submit-btn fsum-button-dark-blue float-right"
                    no-caps
                    :loading="isLoading"
                />
            </div>
        </div>
    </q-form>
</template>
<style scoped>
#school-form {
    max-width: 600px;
    border-radius: 4px;
}
#school-form .warning {
    background: var(--fsum-warning-blue);
    text-align: left;
}
#school-form .warning span.warning-icon {
    color: var(--fsum-secondary-blue);
    vertical-align: middle;
    margin-top: -4.5px;
}
#school-form label.input-label {
    text-align: left;
    font-weight: 700;
    display: block;
}
.checkbox-group {
    display: grid;
}
.q-btn {
    font-size: 16px;
}
.q-input {
    margin-right: 20px;
    padding-bottom: 10px;
}
.cert-list {
    font-size: 16px;
}
</style>
